import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  IconButton, List, ListItemButton, ListItemText, Pagination, Skeleton, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip, Typography,
} from '@mui/material';
import { HoverPopover, SegmentTransactionCard } from '../../pages/TransactionsPage';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, LabelList, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  AccountBalanceWallet, Close, CreditCard, InfoOutlined, LocalAtm,
  Storefront, TrendingDown, TrendingUp,
} from '@mui/icons-material';
import apiService from '../../services/apiService';
import { colors } from '../../theme/theme';

/**
 * Studio analytics — revenue and membership, for the studio OWNER only.
 *
 * Everything here comes from ONE endpoint (`/dashboard/studioAnalytics`), which
 * derives every figure from a single aggregation. That matters: the three
 * revenue calcs already in this product disagree with one another, and adding a
 * fourth that recomputed anything client-side would guarantee a fifth answer.
 * This component does no arithmetic on money beyond formatting — if a number
 * looks wrong the fix belongs in the aggregation, not here.
 *
 * Figures are GROSS (what the member actually paid). The server returns `gst`
 * and `net` alongside, and the header shows both so the owner can see the tax
 * split without the two bases ever being mixed inside one total — which is the
 * flaw in the Transactions page's cards, where Razorpay revenue is net of GST
 * but cash/manual is gross.
 */

const STREAMS = [
  { key: 'membership', label: 'Memberships', color: '#7B1FA2' },
  { key: 'workshops',  label: 'Workshops',   color: '#0288D1' },
  { key: 'classes',    label: 'Classes',     color: colors.primary },
  { key: 'retail',     label: 'Retail',      color: '#f9a825' },
  { key: 'other',      label: 'Other',       color: '#78909c' },
] as const;

const RANGES = [
  { key: '7d',  label: '7 days',   days: 6 },
  { key: '30d', label: '30 days',  days: 29 },
  { key: '90d', label: '90 days',  days: 89 },
  { key: '12m', label: '12 months', days: 364 },
] as const;

// "Revenue by pack" card — its OWN window, independent of the range toggle
// above. Sourced from the transaction ledger (studioAnalyticsPackRevenue),
// the same classification the "Total Revenue" card's Membership stream uses
// — NOT the purchasedpacks collection, which lied about revenue three ways:
// a deleted pack (removed after a cancellation) vanished even though the
// payment was real, a free/comped pack still carried its catalogue price,
// and a since-redeemed pack could show the redemption's ₹0 instead of the
// purchase. What was actually paid for a pack is counted once, on the day
// it was bought (by transaction date): a 3-month pack sold on 10 Sep lands
// entirely in whichever window 10 Sep falls in, never spread across the
// months it covers. Free/comped purchases are excluded outright (same as
// the Membership stream), not shown as ₹0 rows. A pack whose document has
// since been deleted still counts, grouped under "Unknown / deleted pack".
const PACK_RANGES = [
  { key: '7d',     label: '7 days',    days: 6 },
  { key: '30d',    label: '30 days',   days: 29 },
  { key: '90d',    label: '90 days',   days: 89 },
  { key: '12m',    label: '12 months', days: 364 },
  { key: 'custom', label: 'Custom' },
] as const;
type PackRangeKey = (typeof PACK_RANGES)[number]['key'];

// Soft pastel palette, pink-led — one tint per pack, assigned in fixed order
// (never cycled). These are deliberately gentle/decorative: pack identity does
// not rely on colour here — every bar is labelled with its ₹ value at the end,
// the pack name sits on the axis, and there is a 2px gap between bars.
// Order: blush pink, salmon, peach, pistachio, aqua, lavender, orchid, rose.
const PACK_COLORS = [
  '#F4A9C7', '#F7B8A8', '#F6D6A8', '#CFE4B0',
  '#A9D8E0', '#C6B8E8', '#F2A9DC', '#E7B3C4',
];
// A 9th+ pack is not a new hue — the tail folds into one neutral "Other" bar.
const PACK_OTHER_COLOR = '#9E9E9E';

const PackRevenueTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #eee', borderRadius: 2, p: 1.5, boxShadow: 3 }}>
      <Typography variant="caption" fontWeight={700} display="block" mb={0.25}>{p.title}</Typography>
      <Typography variant="caption" display="block"><strong>{inr(p.revenue)}</strong></Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {p.count} purchase{p.count === 1 ? '' : 's'}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        Click bar to view purchasers
      </Typography>
    </Box>
  );
};

/** One purchase, kept alongside the per-pack totals so a bar click can list who bought it. */
type PackPurchaser = {
  id: string;
  userId?: string;
  name: string;
  email?: string;
  phone?: string;
  purchaseDate?: string;
  price: number;
  packTitle: string;
};

const IST = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' });
const istDay = (d: Date) => IST.format(d);

/** Shift a 'YYYY-MM-DD' string by whole days, calendar-safe, no timezone drift. */
const addDaysISO = (iso: string, delta: number) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + delta)).toISOString().slice(0, 10);
};
/** Whole days from a → b (b − a), both 'YYYY-MM-DD'. */
const spanDaysISO = (a: string, b: string) => {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 864e5);
};

/** ₹1,23,456 — Indian grouping, no paise. Paise are noise at this scale. */
const inr = (n: number) =>
  `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
/** Compact form for axis ticks, where the full number will not fit. */
const inrShort = (n: number) => {
  const v = Math.round(Number(n) || 0);
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(1)}Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  if (Math.abs(v) >= 1e3) return `₹${Math.round(v / 1e3)}k`;
  return `₹${v}`;
};

const cardSx = {
  height: '100%',
  borderRadius: 3,
  boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
} as const;

const KpiCard: React.FC<{
  title: string;
  value: string;
  sub?: React.ReactNode;
  tooltip?: string;
  icon: React.ReactNode;
  iconBg: string;
  delta?: number | null;
  loading?: boolean;
}> = ({ title, value, sub, tooltip, icon, iconBg, delta, loading }) => (
  <Card sx={cardSx}>
    <CardContent sx={{ p: 2.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {title}
          </Typography>
          {tooltip && (
            <MuiTooltip title={tooltip} arrow placement="top">
              <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
            </MuiTooltip>
          )}
        </Box>
        <Box
          sx={{
            width: 40, height: 40, borderRadius: 2, bgcolor: iconBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Box>
      {loading ? (
        <Skeleton variant="text" width="70%" height={40} />
      ) : (
        <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
          {value}
        </Typography>
      )}
      <Box display="flex" alignItems="center" gap={1} mt={0.5} minHeight={20} flexWrap="wrap">
        {sub && !loading && (
          typeof sub === 'string'
            ? <Typography variant="caption" color="text.secondary">{sub}</Typography>
            : sub
        )}
        {typeof delta === 'number' && !loading && (
          <Box display="flex" alignItems="center" gap={0.3}>
            {delta >= 0
              ? <TrendingUp sx={{ fontSize: 14, color: colors.success }} />
              : <TrendingDown sx={{ fontSize: 14, color: colors.error }} />}
            <Typography
              variant="caption"
              fontWeight={600}
              color={delta >= 0 ? colors.success : colors.error}
            >
              {delta >= 0 ? '+' : ''}{delta}%
            </Typography>
          </Box>
        )}
      </Box>
    </CardContent>
  </Card>
);

const ChartCard: React.FC<{
  title: string; subtitle?: string; children: React.ReactNode; action?: React.ReactNode;
}> = ({ title, subtitle, children, action }) => (
  <Card sx={cardSx}>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h6" fontWeight={700}>{title}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
          )}
        </Box>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const MoneyTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p: any) => p.value > 0);
  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #eee', borderRadius: 2, p: 1.5, boxShadow: 3 }}>
      <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>{label}</Typography>
      {rows.length === 0 && (
        <Typography variant="caption" color="text.secondary">No revenue</Typography>
      )}
      {rows.map((p: any) => (
        <Box key={p.dataKey} display="flex" alignItems="center" gap={1}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color }} />
          <Typography variant="caption">
            {p.name}: <strong>{inr(p.value)}</strong>
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// Hover breakdown for the "Total Revenue" KPI card — same interaction pattern
// (HoverPopover) as the Transactions page's own Total Revenue card, but built
// from THIS page's own already-computed byStream data rather than reusing
// that page's aggregate. Wiring the two together would put a second,
// differently-GST-computed number on this card (see the file header comment)
// — exactly the disagreement this page exists to avoid.
//
// Structured the same way Dashboard's own Total Revenue popup is (Razorpay
// section, then Cash/Manual section, then a reconciling grand total) — each
// section's row is that stream's money in JUST that payment mode, with its
// own transaction count, so a stream paid for by both modes (e.g. 10 online +
// 21 cash) shows two clickable counts instead of one combined, misleading one.
//
// Streams whose transaction list can actually be drilled into — the owner
// asked for Membership and Classes specifically; Workshops/Retail/Other stay
// display-only rows for now (the backend endpoint behind this handles any
// stream identically, so adding them later is just adding the key here).
const CLICKABLE_STREAMS = new Set(['membership', 'classes']);

type StreamRow = {
  key: string; label: string; color: string;
  online: number; onlineCount: number;
  cashManual: number; cashManualCount: number;
};

const AnalyticsRevenuePopup: React.FC<{
  streamRows: StreamRow[];
  gross: number;
  loading?: boolean;
  onStreamClick?: (stream: { key: string; label: string; mode: 'online' | 'cash' }) => void;
}> = ({ streamRows, gross, loading, onStreamClick }) => {
  const onlineStreamRows = streamRows.filter((s) => s.online > 0);
  const cashStreamRows = streamRows.filter((s) => s.cashManual > 0);
  if (loading) {
    return (
      <Box sx={{ width: 260, p: 2.5, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (onlineStreamRows.length === 0 && cashStreamRows.length === 0) {
    return (
      <Box sx={{ width: 260, p: 2 }}>
        <Typography variant="body2" color="text.secondary">No revenue in this period</Typography>
      </Box>
    );
  }
  const streamRowBox = (
    s: StreamRow,
    amount: number,
    count: number,
    mode: 'online' | 'cash',
  ) => {
    const clickable = CLICKABLE_STREAMS.has(s.key) && !!onStreamClick;
    return (
      <Box
        key={s.key}
        display="flex" justifyContent="space-between" alignItems="center" py={0.75}
        onClick={clickable ? () => onStreamClick!({ key: s.key, label: s.label, mode }) : undefined}
        sx={clickable ? { cursor: 'pointer', borderRadius: 1, mx: -1, px: 1, '&:hover': { bgcolor: colors.backgroundTertiary } } : undefined}
      >
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
          <Typography variant="body2" noWrap>{s.label}</Typography>
        </Box>
        <Box textAlign="right" flexShrink={0}>
          <Typography variant="body2" fontWeight={700}>{inr(amount)}</Typography>
          <Typography
            variant="caption" color={colors.primary} fontWeight={600}
            sx={clickable ? { textDecoration: 'underline', textUnderlineOffset: 2 } : undefined}
          >
            {count} txn{count === 1 ? '' : 's'}
          </Typography>
        </Box>
      </Box>
    );
  };
  return (
    <Box sx={{ width: 260, maxWidth: '92vw' }}>
      {onlineStreamRows.length > 0 && (
        <>
          <Box sx={{ px: 1.75, py: 0.75, bgcolor: `${colors.primary}0D`, borderBottom: `1px solid ${colors.divider}` }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Razorpay
            </Typography>
          </Box>
          <Box sx={{ px: 1.75, py: 0.5 }}>
            {onlineStreamRows.map((s) => streamRowBox(s, s.online, s.onlineCount, 'online'))}
          </Box>
        </>
      )}
      {/* Same "Cash / Manual" breakdown the Transactions page's own Cash/
          Manual card shows — that one splits by studio/gateway (an All-tab,
          multi-studio view); this page is always one studio already, so the
          equivalent split here is by stream instead. */}
      {cashStreamRows.length > 0 && (
        <>
          <Box sx={{ px: 1.75, py: 0.75, bgcolor: `${colors.success}14`, borderTop: `1px solid ${colors.divider}`, borderBottom: `1px solid ${colors.divider}` }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Manual
            </Typography>
          </Box>
          <Box sx={{ px: 1.75, py: 0.5 }}>
            {cashStreamRows.map((s) => streamRowBox(s, s.cashManual, s.cashManualCount, 'cash'))}
          </Box>
        </>
      )}
      <Box sx={{ px: 1.75, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: colors.backgroundTertiary, borderTop: `1px solid ${colors.divider}` }}>
        <Typography variant="body2" fontWeight={700}>Total</Typography>
        <Typography variant="body1" fontWeight={700} color={colors.primary}>{inr(gross)}</Typography>
      </Box>
    </Box>
  );
};

const STREAM_SEGMENT_PAGE_SIZE = 25;

// Transaction-history drill-down for one clicked stream row (Membership /
// Classes) in AnalyticsRevenuePopup — same per-row card (SegmentTransactionCard)
// the Transactions page's own drill-downs use, "like all other places", just
// fed by studioAnalyticsSegment (stream-classified) instead of that page's
// gateway-bucket endpoint.
const STREAM_MODE_LABEL: Record<string, string> = { online: 'Razorpay', cash: 'Manual' };

const StreamTransactionsDialog: React.FC<{
  open: boolean;
  stream: { key: string; label: string; mode?: 'online' | 'cash' } | null;
  startDate?: string;
  endDate?: string;
  onClose: () => void;
}> = ({ open, stream, startDate, endDate, onClose }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const fetchIdRef = useRef(0);

  const loadPage = useCallback(
    (p: number) => {
      if (!stream) return;
      const fetchId = ++fetchIdRef.current;
      setLoading(true);
      setErr(null);
      apiService
        .getStudioAnalyticsSegment({
          stream: stream.key,
          mode: stream.mode,
          startDate, endDate,
          page: p, pageSize: STREAM_SEGMENT_PAGE_SIZE,
        })
        .then((res: any) => {
          if (fetchId !== fetchIdRef.current) return;
          setRows(res.transactions || []);
          setPages(res.pages || 1);
          setCount(res.count || 0);
          setPage(p);
        })
        .catch((e: any) => {
          if (fetchId !== fetchIdRef.current) return;
          setErr(e?.response?.data?.message || e?.message || 'Failed to load transactions');
        })
        .finally(() => {
          if (fetchId === fetchIdRef.current) setLoading(false);
        });
    },
    [stream, startDate, endDate],
  );

  useEffect(() => {
    if (!open) return;
    loadPage(1);
  }, [open, loadPage]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {stream
          ? `${stream.label}${stream.mode ? ` — ${STREAM_MODE_LABEL[stream.mode]}` : ''} transactions`
          : 'Transactions'}
        <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
          {count} transaction{count !== 1 ? 's' : ''}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Box py={5} textAlign="center">
            <Typography color="text.secondary">No transactions</Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={1.5}>
            {rows.map((t: any) => <SegmentTransactionCard key={t._id} t={t} />)}
            {pages > 1 && (
              <Box display="flex" justifyContent="center" pt={1}>
                <Pagination
                  count={pages}
                  page={page}
                  onChange={(_, p) => { if (p !== page) loadPage(p); }}
                  color="primary"
                  size="small"
                  disabled={loading}
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const StudioAnalytics: React.FC = () => {
  const [rangeKey, setRangeKey] = useState<string>('30d');
  const [data, setData] = useState<any>(null);
  const [prev, setPrev] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Set when a Membership/Classes row in the Total Revenue popup is clicked —
  // drives StreamTransactionsDialog below.
  const [streamDialog, setStreamDialog] = useState<{ key: string; label: string; mode?: 'online' | 'cash' } | null>(null);

  // Custom range for the main Analytics filter (the 7d/30d/90d/12m toggle).
  // Seeded to the last 30 days so switching to "Custom" is never blank.
  const [customStart, setCustomStart] = useState<string>(() =>
    istDay(new Date(Date.now() - 29 * 864e5)),
  );
  const [customEnd, setCustomEnd] = useState<string>(() => istDay(new Date()));

  // Resolves the toggle (or the two custom pickers) into the current window
  // plus the immediately-preceding window of the same length, used for the
  // KPI deltas. `null` while a custom range is incomplete/inverted.
  const analyticsWindow = useMemo(() => {
    if (rangeKey === 'custom') {
      if (!customStart || !customEnd || customStart > customEnd) return null;
      const span = spanDaysISO(customStart, customEnd);
      return {
        startDate: customStart,
        endDate: customEnd,
        prevEnd: addDaysISO(customStart, -1),
        prevStart: addDaysISO(customStart, -(span + 1)),
      };
    }
    const days = RANGES.find((r) => r.key === rangeKey)?.days ?? 29;
    const now = new Date();
    return {
      startDate: istDay(new Date(now.getTime() - days * 864e5)),
      endDate: istDay(now),
      prevEnd: istDay(new Date(now.getTime() - (days + 1) * 864e5)),
      prevStart: istDay(new Date(now.getTime() - (days * 2 + 1) * 864e5)),
    };
  }, [rangeKey, customStart, customEnd]);

  const fetchData = useCallback(async () => {
    if (!analyticsWindow) return;
    setLoading(true);
    setError(null);
    const { startDate, endDate, prevStart, prevEnd } = analyticsWindow;
    try {
      const [cur, before] = await Promise.all([
        apiService.getStudioAnalytics({ startDate, endDate }),
        apiService.getStudioAnalytics({ startDate: prevStart, endDate: prevEnd }),
      ]);
      setData(cur);
      setPrev(before);
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || 'Could not load analytics',
      );
    } finally {
      setLoading(false);
    }
  }, [analyticsWindow]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── "Revenue by pack" card — self-contained, own window ────────────────
  // Nothing here touches `rangeKey`/`fetchData`, and nothing above depends on
  // this state, so changing this toggle only re-renders this one card.
  const [packRangeKey, setPackRangeKey] = useState<PackRangeKey>('30d');
  const [packCustom, setPackCustom] = useState<{ start: string; end: string }>(() => {
    const now = new Date();
    return { start: istDay(new Date(now.getTime() - 29 * 864e5)), end: istDay(now) };
  });
  const [packRows, setPackRows] = useState<{ title: string; revenue: number; count: number; purchasers: PackPurchaser[] }[]>([]);
  const [packLoading, setPackLoading] = useState(true);
  const [packError, setPackError] = useState<string | null>(null);
  // Set when a bar is clicked — drives the "who bought this" dialog below.
  const [packUsersDialog, setPackUsersDialog] = useState<{ title: string; purchasers: PackPurchaser[] } | null>(null);
  const navigate = useNavigate();

  const packWindow = useMemo(() => {
    if (packRangeKey === 'custom') return { startDate: packCustom.start, endDate: packCustom.end };
    const days: Record<string, number> = { '7d': 6, '30d': 29, '90d': 89, '12m': 364 };
    const now = new Date();
    return {
      startDate: istDay(new Date(now.getTime() - (days[packRangeKey] ?? 29) * 864e5)),
      endDate: istDay(now),
    };
  }, [packRangeKey, packCustom]);

  const fetchPackRevenue = useCallback(async () => {
    const { startDate, endDate } = packWindow;
    if (!startDate || !endDate || startDate > endDate) {
      setPackRows([]);
      setPackLoading(false);
      return;
    }
    setPackLoading(true);
    setPackError(null);
    try {
      // Sourced from the transaction ledger (studioAnalyticsPackRevenue),
      // not the purchasedpacks collection — see packRevenueByTitle's doc
      // comment for why: a deleted pack, a free/comped one, and a since-
      // redeemed one all lied about revenue when read off the pack document
      // itself. The backend already groups by title and excludes
      // redemptions/free rows, so no client-side aggregation is needed here.
      const res = await apiService.getStudioPackRevenue({ startDate, endDate });
      const rows: { title: string; revenue: number; count: number; purchasers: PackPurchaser[] }[] = res.rows || [];
      setPackRows(rows.map((r) => ({ ...r, revenue: Math.round(r.revenue) })));
    } catch (e: any) {
      setPackError(e?.response?.data?.message || e?.message || 'Could not load pack revenue');
    } finally {
      setPackLoading(false);
    }
  }, [packWindow]);

  useEffect(() => { fetchPackRevenue(); }, [fetchPackRevenue]);

  const packTotal = useMemo(() => packRows.reduce((s, p) => s + p.revenue, 0), [packRows]);
  const packPurchases = useMemo(() => packRows.reduce((s, p) => s + p.count, 0), [packRows]);

  // The bar chart shows at most PACK_COLORS.length bars: the top packs by
  // revenue, plus one grey "Other" bar for the rest, so a hue is never reused.
  const packChartRows = useMemo(() => {
    if (packRows.length <= PACK_COLORS.length) return packRows;
    const top = packRows.slice(0, PACK_COLORS.length - 1);
    const rest = packRows.slice(PACK_COLORS.length - 1);
    return [
      ...top,
      {
        title: `Other · ${rest.length} packs`,
        revenue: rest.reduce((s, p) => s + p.revenue, 0),
        count: rest.reduce((s, p) => s + p.count, 0),
        purchasers: rest.flatMap((p) => p.purchasers),
        isOther: true,
      },
    ];
  }, [packRows]);
  const packChartHeight = Math.max(180, packChartRows.length * 44 + 32);

  const rev = data?.revenue;
  const mem = data?.membership;

  const delta = (cur?: number, before?: number) => {
    if (!before || before <= 0) return null;
    return Math.round((((cur ?? 0) - before) / before) * 100);
  };

  /** Trend, bucketed to weeks once a daily series would be unreadable. */
  const trend = useMemo(() => {
    const raw: any[] = rev?.trend ?? [];
    if (raw.length <= 45) {
      return raw.map((d) => ({
        ...d,
        label: new Date(`${d.date}T00:00:00+05:30`).toLocaleDateString('en-IN', {
          day: 'numeric', month: 'short',
        }),
      }));
    }
    const buckets: any[] = [];
    for (let i = 0; i < raw.length; i += 7) {
      const slice = raw.slice(i, i + 7);
      const sum = (k: string) => slice.reduce((a, d) => a + (d[k] || 0), 0);
      buckets.push({
        label: new Date(`${slice[0].date}T00:00:00+05:30`).toLocaleDateString('en-IN', {
          day: 'numeric', month: 'short',
        }),
        gross: sum('gross'), online: sum('online'), cashManual: sum('cashManual'),
        membership: sum('membership'), workshops: sum('workshops'),
        classes: sum('classes'), retail: sum('retail'),
      });
    }
    return buckets;
  }, [rev]);

  const streamRows = useMemo(
    () =>
      STREAMS
        .map((s) => ({ ...s, ...(rev?.byStream?.[s.key] ?? { gross: 0, count: 0 }) }))
        .filter((s) => s.gross > 0 || s.count > 0)
        .sort((a, b) => b.gross - a.gross),
    [rev],
  );

  if (error) {
    return <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>;
  }

  const gross = rev?.gross ?? 0;
  const online = rev?.online?.gross ?? 0;
  const cash = rev?.cashManual?.gross ?? 0;
  const pct = (n: number) => (gross > 0 ? Math.round((n / gross) * 100) : 0);

  // Merchandise gets its own card, but ONLY when the studio actually sells any —
  // an always-present ₹0 Retail box on a studio with no shop is noise.
  //
  // It is a CROSS-CUT of the totals, not another slice beside Online and
  // Cash/Manual: a retail sale is already inside whichever of those two paid
  // for it, and inside Total Revenue. Labelled "included above" so nobody adds
  // it on. Merch is otherwise easy to miss entirely — it is deliberately kept
  // out of studio payouts (it settles directly), so it appears on no other
  // revenue screen in the product.
  const retail = rev?.byStream?.retail ?? { gross: 0, count: 0 };
  const showRetail = (retail.gross ?? 0) > 0 || (retail.count ?? 0) > 0;

  return (
    <Box mb={4}>
      <Box
        display="flex" justifyContent="space-between" alignItems="flex-end"
        flexWrap="wrap" gap={2} mb={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>Analytics</Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.range
              ? `${data.range.startDate} → ${data.range.endDate} · all revenue, online and cash/manual`
              : 'Loading…'}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
          <ToggleButtonGroup
            size="small" exclusive value={rangeKey}
            onChange={(_, v) => v && setRangeKey(v)}
          >
            {RANGES.map((r) => (
              <ToggleButton key={r.key} value={r.key} sx={{ px: 2, textTransform: 'none' }}>
                {r.label}
              </ToggleButton>
            ))}
            <ToggleButton value="custom" sx={{ px: 2, textTransform: 'none' }}>
              Custom
            </ToggleButton>
          </ToggleButtonGroup>
          {rangeKey === 'custom' && (
            <Box display="flex" gap={1}>
              <TextField
                type="date" size="small" label="From" sx={{ width: 150 }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: customEnd || undefined }}
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
              />
              <TextField
                type="date" size="small" label="To" sx={{ width: 150 }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: customStart || undefined }}
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* ── KPI row ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: showRetail ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)',
          },
          gap: 3, mb: 3,
        }}
      >
        <HoverPopover
          anchor={
            <KpiCard
              title="Total Revenue" loading={loading}
              value={inr(rev?.net ?? 0)}
              sub={
                <Box display="flex" flexDirection="column" gap={0.25}>
                  {(rev?.gst ?? 0) > 0 && (
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      + GST {inr(rev.gst)} = {inr(gross)} total
                    </Typography>
                  )}
                  <Typography variant="caption" color={colors.primary} fontWeight={500}>
                    Hover for breakdown
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    <Box component="span" fontWeight={700}>Note:</Box> cancelled bookings aren't included in this total.
                  </Typography>
                  {(rev?.cancelled?.count ?? 0) > 0 && (
                    <Typography
                      variant="caption" color="text.secondary"
                      onClick={(e) => { e.stopPropagation(); setStreamDialog({ key: 'cancelled', label: 'Cancelled' }); }}
                      sx={{ cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2, width: 'fit-content' }}
                    >
                      {rev.cancelled.count} cancelled
                    </Typography>
                  )}
                </Box>
              }
              delta={delta(gross, prev?.revenue?.gross)}
              icon={<AccountBalanceWallet sx={{ color: '#fff', fontSize: 22 }} />}
              iconBg={colors.primary}
            />
          }
        >
          <AnalyticsRevenuePopup
            streamRows={streamRows}
            gross={gross}
            loading={loading}
            onStreamClick={setStreamDialog}
          />
        </HoverPopover>
        <KpiCard
          title="Online" loading={loading}
          value={inr(rev?.online?.net ?? 0)}
          sub={
            <Box display="flex" flexDirection="column" gap={0.25}>
              {(rev?.online?.gst ?? 0) > 0 && (
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  + GST {inr(rev.online.gst)} = {inr(online)} total
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {pct(online)}% · {rev?.online?.count ?? 0} payments
              </Typography>
            </Box>
          }
          delta={delta(online, prev?.revenue?.online?.gross)}
          icon={<CreditCard sx={{ color: '#fff', fontSize: 22 }} />}
          iconBg="#0288D1"
        />
        <KpiCard
          title="Cash / Manual" loading={loading}
          value={inr(cash)}
          sub={
            <Box display="flex" flexDirection="column" gap={0.25}>
              <Typography variant="caption" color="text.secondary">
                {pct(cash)}% · {rev?.cashManual?.count ?? 0} payments
              </Typography>
              {(rev?.cancelled?.count ?? 0) > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {rev.cancelled.count} cancelled
                </Typography>
              )}
            </Box>
          }
          delta={delta(cash, prev?.revenue?.cashManual?.gross)}
          icon={<LocalAtm sx={{ color: '#fff', fontSize: 22 }} />}
          iconBg={colors.success}
        />
        {showRetail && (
          <KpiCard
            title="Merchandise" loading={loading}
            value={inr(retail.gross)}
            sub={`${retail.count} sale${retail.count === 1 ? '' : 's'} · included above`}
            icon={<Storefront sx={{ color: '#fff', fontSize: 22 }} />}
            iconBg="#f9a825"
          />
        )}
      </Box>

      {/* ── Where members booked ────────────────────────────────────────────
          Only meaningful for a studio that runs its own app AND is listed in
          the SweatSocial app — Purple Pilates is both. Attribution across this
          whole page is by STUDIO, so both channels are already inside every
          figure above; this splits what is there rather than filtering it. */}
      {!loading && rev?.byChannel?.hasOwnApp && (
        <Card sx={{ ...cardSx, mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5} flexWrap="wrap" gap={1}>
              <Box>
                <Typography variant="h6" fontWeight={700}>Where members booked</Typography>
                <Typography variant="caption" color="text.secondary">
                  Your own app vs your listing on SweatSocial
                </Typography>
              </Box>
            </Box>
            {(() => {
              const own = rev.byChannel.ownApp;
              const ss = rev.byChannel.sweatSocial;
              const tot = own.gross + ss.gross;
              const ownPct = tot > 0 ? Math.round((own.gross / tot) * 100) : 0;
              return (
                <>
                  <Box sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', mb: 1.5, bgcolor: '#f1f1f1' }}>
                    <Box sx={{ width: `${ownPct}%`, bgcolor: colors.primary }} />
                    <Box sx={{ width: `${100 - ownPct}%`, bgcolor: '#0288D1' }} />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    {[
                      { label: 'Your own app', v: own, color: colors.primary, pctOf: ownPct },
                      { label: 'SweatSocial app', v: ss, color: '#0288D1', pctOf: 100 - ownPct },
                    ].map((c) => (
                      <Box key={c.label} display="flex" alignItems="center" gap={1.5}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c.color, flexShrink: 0 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600}>{c.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.v.count} txns · {c.pctOf}% of revenue
                          </Typography>
                        </Box>
                        <Typography variant="h6" fontWeight={700}>{inr(c.v.gross)}</Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* ── Trend + split ───────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
          gap: 3, mb: 3,
        }}
      >
        <ChartCard
          title="Revenue over time"
          subtitle={trend.length > 45 ? 'Weekly totals' : 'Daily totals'}
        >
          {loading ? (
            <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gOnline" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0288D1" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#0288D1" stopOpacity={0.04} />
                  </linearGradient>
                  <linearGradient id="gCash" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colors.success} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={colors.success} stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={18} />
                <YAxis tickFormatter={inrShort} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={58} />
                <Tooltip content={<MoneyTooltip />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                {/* Stacked, so the band height is total revenue and the split
                    between the two payment routes is readable at the same time. */}
                <Area type="monotone" dataKey="online" name="Online" stackId="1"
                      stroke="#0288D1" fill="url(#gOnline)" strokeWidth={2} />
                <Area type="monotone" dataKey="cashManual" name="Manual" stackId="1"
                      stroke={colors.success} fill="url(#gCash)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Where it came from" subtitle="Share of total revenue">
          {loading ? (
            <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
          ) : streamRows.length === 0 ? (
            <Box height={280} display="flex" alignItems="center" justifyContent="center">
              <Typography variant="body2" color="text.secondary">
                No revenue in this period
              </Typography>
            </Box>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie
                    data={streamRows} dataKey="gross" nameKey="label"
                    innerRadius={54} outerRadius={82} paddingAngle={2} stroke="none"
                  >
                    {streamRows.map((s) => <Cell key={s.key} fill={s.color} />)}
                  </Pie>
                  <Tooltip content={<MoneyTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <Box display="flex" flexDirection="column" gap={0.75} mt={1}>
                {streamRows.map((s) => (
                  <Box key={s.key} display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ flex: 1 }}>{s.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.count}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ minWidth: 74, textAlign: 'right' }}>
                      {inr(s.gross)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 34, textAlign: 'right' }}>
                      {pct(s.gross)}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </ChartCard>
      </Box>

      {/* ── Stream × payment route, and membership health ───────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
          gap: 3,
        }}
      >
        <ChartCard
          title="Revenue stream by payment route"
          subtitle="Each stream split into online and cash/manual"
        >
          {loading ? (
            <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={streamRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tickFormatter={inrShort} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={58} />
                <Tooltip content={<MoneyTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="online" name="Online" stackId="a" fill="#0288D1" radius={[0, 0, 0, 0]} maxBarSize={54} />
                <Bar dataKey="cashManual" name="Manual" stackId="a" fill={colors.success} radius={[6, 6, 0, 0]} maxBarSize={54} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard
          title="Memberships"
          subtitle="Live counts, not affected by the date range"
        >
          {loading ? (
            <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
          ) : (
            <Box>
              <Box
                sx={{
                  display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, mb: 2,
                }}
              >
                {[
                  { label: 'Active', value: mem?.active ?? 0, color: colors.success },
                  {
                    label: 'Expiring', value: mem?.expiringSoon ?? 0, color: '#f9a825',
                    hint: 'Active memberships expiring within 30 days. A subset of Active, not an extra group.',
                  },
                  { label: 'Upcoming', value: mem?.upcoming ?? 0, color: '#0288D1' },
                  { label: 'Expired', value: mem?.expired ?? 0, color: '#90a4ae' },
                ].map((m) => (
                  <Box
                    key={m.label}
                    sx={{
                      p: 1.5, borderRadius: 2, border: '1px solid #eee',
                      borderLeft: `3px solid ${m.color}`,
                    }}
                  >
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>
                      {m.value}
                    </Typography>
                    {m.hint ? (
                      <MuiTooltip title={m.hint}>
                        <Typography
                          variant="caption" color="text.secondary"
                          sx={{ borderBottom: '1px dotted #bbb', cursor: 'help' }}
                        >
                          {m.label}
                        </Typography>
                      </MuiTooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">{m.label}</Typography>
                    )}
                  </Box>
                ))}
              </Box>

              {mem?.frozen > 0 && (
                <Chip
                  size="small" label={`${mem.frozen} frozen`}
                  sx={{ mb: 1.5, bgcolor: '#eceff1', fontWeight: 600 }}
                />
              )}

              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                ACTIVE BY PACK
              </Typography>
              <Box mt={1} display="flex" flexDirection="column" gap={0.75}>
                {(mem?.activeByPack ?? []).length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No active memberships
                  </Typography>
                )}
                {(mem?.activeByPack ?? []).slice(0, 6).map((p: any) => (
                  <Box key={p.title} display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" noWrap sx={{ flex: 1 }} title={p.title}>
                      {p.title}
                    </Typography>
                    <Typography variant="caption" fontWeight={700}>{p.count}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </ChartCard>
      </Box>

      {/* ── Revenue by pack — own window, isolated from everything above ─── */}
      <Box mt={3}>
        <ChartCard
          title="Revenue by pack"
          subtitle="What was actually paid for each pack, counted on its purchase date"
          action={
            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
              <ToggleButtonGroup
                size="small" exclusive value={packRangeKey}
                onChange={(_, v) => v && setPackRangeKey(v)}
              >
                {PACK_RANGES.map((r) => (
                  <ToggleButton key={r.key} value={r.key} sx={{ px: 2, textTransform: 'none' }}>
                    {r.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {packRangeKey === 'custom' && (
                <Box display="flex" gap={1}>
                  <TextField
                    type="date" size="small" label="From" sx={{ width: 150 }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: packCustom.end || undefined }}
                    value={packCustom.start}
                    onChange={(e) => setPackCustom((c) => ({ ...c, start: e.target.value }))}
                  />
                  <TextField
                    type="date" size="small" label="To" sx={{ width: 150 }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: packCustom.start || undefined }}
                    value={packCustom.end}
                    onChange={(e) => setPackCustom((c) => ({ ...c, end: e.target.value }))}
                  />
                </Box>
              )}
            </Box>
          }
        >
          {packLoading ? (
            <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
          ) : packError ? (
            <Alert severity="error">{packError}</Alert>
          ) : packRows.length === 0 ? (
            <Box height={180} display="flex" alignItems="center" justifyContent="center">
              <Typography variant="body2" color="text.secondary">
                No pack purchases in this period
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                {inr(packTotal)} · {packPurchases} purchase{packPurchases === 1 ? '' : 's'} · {packRows.length} pack{packRows.length === 1 ? '' : 's'}
              </Typography>
              <ResponsiveContainer width="100%" height={packChartHeight}>
                <BarChart
                  data={packChartRows} layout="vertical"
                  margin={{ top: 4, right: 72, left: 8, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                  <XAxis
                    type="number" tickFormatter={inrShort}
                    tick={{ fontSize: 11 }} tickLine={false} axisLine={false}
                  />
                  <YAxis
                    type="category" dataKey="title" width={140}
                    tick={{ fontSize: 11 }} tickLine={false} axisLine={false}
                    tickFormatter={(v: string) => (v.length > 20 ? `${v.slice(0, 19)}…` : v)}
                  />
                  <Tooltip content={<PackRevenueTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                  <Bar
                    dataKey="revenue" name="Revenue" radius={[0, 6, 6, 0]} maxBarSize={34}
                    onClick={(p: any) => setPackUsersDialog({ title: p.title, purchasers: p.purchasers || [] })}
                  >
                    {packChartRows.map((p: any, i) => (
                      <Cell
                        key={p.title}
                        fill={p.isOther ? PACK_OTHER_COLOR : PACK_COLORS[i]}
                        cursor="pointer"
                      />
                    ))}
                    <LabelList
                      dataKey="revenue" position="right"
                      formatter={(v: any) => inr(v)}
                      style={{ fontSize: 11, fontWeight: 700, fill: colors.textSecondary }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
        </ChartCard>
      </Box>

      {/* ── Who bought this pack — opened by clicking a "Revenue by pack" bar ── */}
      <Dialog
        open={!!packUsersDialog}
        onClose={() => setPackUsersDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {packUsersDialog?.title}
          <Typography variant="caption" color="text.secondary" display="block">
            {packUsersDialog?.purchasers.length ?? 0} purchase{(packUsersDialog?.purchasers.length ?? 0) === 1 ? '' : 's'}
          </Typography>
          <IconButton
            onClick={() => setPackUsersDialog(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List dense disablePadding>
            {packUsersDialog?.purchasers.map((u) => (
              <ListItemButton
                key={u.id}
                disabled={!u.userId}
                onClick={() => {
                  if (!u.userId) return;
                  setPackUsersDialog(null);
                  navigate(`/users/${u.userId}`);
                }}
              >
                <ListItemText
                  primary={u.name}
                  secondary={[
                    u.email,
                    u.purchaseDate ? new Date(u.purchaseDate).toLocaleDateString('en-IN') : null,
                    packUsersDialog && packUsersDialog.title.startsWith('Other') ? u.packTitle : null,
                  ].filter(Boolean).join(' · ')}
                />
                <Typography variant="body2" fontWeight={700} sx={{ ml: 2, whiteSpace: 'nowrap' }}>
                  {inr(u.price)}
                </Typography>
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      <StreamTransactionsDialog
        open={!!streamDialog}
        stream={streamDialog}
        startDate={data?.range?.startDate}
        endDate={data?.range?.endDate}
        onClose={() => setStreamDialog(null)}
      />
    </Box>
  );
};

export default StudioAnalytics;
